import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";

import useUnreadToken from "common/datahooks/useUnreadToken";

interface UnreadMessagesWebsocketProviderProps {
  children: ReactNode;
}

const UnreadMessagesWebsocketContext = createContext<WebSocket>(null);

const wsBaseUrl = import.meta.env.VITE_INBOX_WS_URL;

const MAX_RETRIES = 5;
const BASE_DELAY_MS = 1000;

export function UnreadMessagesWebsocketProvider({
  children,
}: UnreadMessagesWebsocketProviderProps) {
  const { unreadToken } = useUnreadToken();
  const [websocket, setWebsocket] = useState<WebSocket>();
  const websocketRef = useRef<WebSocket>();
  const retryCount = useRef(0);
  const retryTimeout = useRef<ReturnType<typeof setTimeout>>();
  const closedIntentionally = useRef(false);

  useEffect(() => {
    if (!unreadToken) return;

    closedIntentionally.current = false;
    retryCount.current = 0;

    function connect() {
      const websocketUrl = `${wsBaseUrl}/unread?authorization=${unreadToken}`;
      const ws = new WebSocket(websocketUrl);

      ws.onopen = () => {
        retryCount.current = 0;
      };

      ws.onclose = () => {
        if (closedIntentionally.current) return;

        if (retryCount.current < MAX_RETRIES) {
          const delay = BASE_DELAY_MS * 2 ** retryCount.current;
          retryCount.current += 1;
          retryTimeout.current = setTimeout(() => {
            const newWs = connect();
            setWebsocket(newWs);
          }, delay);
        } else {
          toast.error(
            "Loading unread messages failed, please refresh the page",
          );
        }
      };

      websocketRef.current = ws;
      return ws;
    }

    setWebsocket(connect());

    function onVisibilityChange() {
      if (document.visibilityState !== "visible") return;

      const current = websocketRef.current;
      if (
        current &&
        current.readyState !== WebSocket.OPEN &&
        current.readyState !== WebSocket.CONNECTING
      ) {
        retryCount.current = 0;
        clearTimeout(retryTimeout.current);

        const newWs = connect();
        setWebsocket(newWs);
      }
    }

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      closedIntentionally.current = true;
      clearTimeout(retryTimeout.current);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      setWebsocket((prev) => {
        if (
          prev &&
          (prev.readyState === WebSocket.OPEN ||
            prev.readyState === WebSocket.CONNECTING)
        ) {
          prev.close();
        }
        return undefined;
      });
    };
  }, [unreadToken]);

  return (
    <UnreadMessagesWebsocketContext.Provider value={websocket}>
      {children}
    </UnreadMessagesWebsocketContext.Provider>
  );
}

export function useUnreadMessagesWebsocketContext(
  onDataReceived: (data: {
    unreadCount: number;
    shouldRefetch: boolean;
  }) => void,
) {
  const unreadMessagesWebsocket = useContext(UnreadMessagesWebsocketContext);

  useEffect(() => {
    if (unreadMessagesWebsocket) {
      function onMessage(event: MessageEvent) {
        const { unread_count: unreadCount, refetch: shouldRefetch } =
          JSON.parse(event.data);
        onDataReceived({ unreadCount, shouldRefetch });
      }

      unreadMessagesWebsocket.addEventListener("message", onMessage);
      return () => {
        unreadMessagesWebsocket.removeEventListener("message", onMessage);
      };
    }
  }, [unreadMessagesWebsocket, onDataReceived]);

  return { unreadMessagesWebsocket };
}
